import { getUnifiedToken } from "./unifiedAuth";
const AUTH_CACHE_KEY = "ava_auth_me_cache_v1";
const AUTH_CACHE_TTL_MS = 8000;

const OFFLINE_USER_KEY =
  "avaaio_last_verified_offline_user_v1";

export type OfflineVerifiedUser = {
  email?: string;

  // Security authority is intentionally restricted offline.
  role: "user";

  // Display and local-feature entitlement only.
  // This never grants administrator routes or server permissions.
  offline_entitlement?: "admin" | "pro" | "free";

  // Used only to keep locally supported premium cards available.
  subscription_status?: "active" | "free";

  status?: string;
  preferred_language?: string;
  verified_at: number;
};

export function readLastVerifiedOfflineUser():
  OfflineVerifiedUser | null {
  try {
    const raw =
      localStorage.getItem(OFFLINE_USER_KEY);

    if (!raw) return null;

    const parsed =
      JSON.parse(raw) as OfflineVerifiedUser;

    if (
      !parsed ||
      parsed.role !== "user" ||
      !Number.isFinite(parsed.verified_at)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writeLastVerifiedOfflineUser(
  user: CachedAuthUser
): void {
  try {
    const status =
      String(user?.status || "active")
        .trim()
        .toLowerCase();

    if (
      status === "banned" ||
      status === "denied" ||
      status === "deleted"
    ) {
      localStorage.removeItem(
        OFFLINE_USER_KEY
      );
      return;
    }

    const normalizedRole =
      String((user as any)?.role || "")
        .trim()
        .toLowerCase();

    const normalizedSubscription =
      String(
        (user as any)?.subscription_status ||
        (user as any)?.subscription ||
        (user as any)?.plan ||
        ""
      )
        .trim()
        .toLowerCase();

    const isVerifiedAdmin = [
      "admin",
      "owner",
      "superadmin",
      "super_admin",
    ].includes(normalizedRole);

    const isVerifiedPro =
      isVerifiedAdmin ||
      normalizedRole === "vip" ||
      normalizedRole === "pro" ||
      Boolean((user as any)?.vip_lifetime) ||
      normalizedSubscription === "active" ||
      normalizedSubscription === "pro";

    const record: OfflineVerifiedUser = {
      email:
        typeof user?.email === "string"
          ? user.email
          : undefined,

      // Never persist administrator authority offline.
      role: "user",

      // Entitlement affects only local display and locally
      // available features. It grants no admin authority.
      offline_entitlement:
        isVerifiedAdmin
          ? "admin"
          : isVerifiedPro
            ? "pro"
            : "free",

      subscription_status:
        isVerifiedPro ? "active" : "free",

      status:
        typeof user?.status === "string"
          ? user.status
          : "active",

      preferred_language:
        typeof user?.preferred_language ===
        "string"
          ? user.preferred_language
          : undefined,

      verified_at: Date.now(),
    };

    localStorage.setItem(
      OFFLINE_USER_KEY,
      JSON.stringify(record)
    );
  } catch {}
}

const API_BASE = String(
  import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_URL ||
    "https://api.avaaio.com"
).replace(/\/+$/, "");

function resolveAuthUrl(
  url: string
): string {
  const value = String(url || "").trim();

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (!value) {
    return `${API_BASE}/api/v1/auth/me`;
  }

  return `${API_BASE}${
    value.startsWith("/")
      ? value
      : `/${value}`
  }`;
}


export type CachedAuthUser = {
  role?: string;
  email?: string;
  status?: string;
  subscription_status?: string;
  memory?: any;
  [key: string]: any;
};

type CachePayload = {
  ts: number;
  user: CachedAuthUser | null;
};

export function readCachedAuthUser(): CachedAuthUser | null {
  try {
    const raw = sessionStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachePayload;

    if (!parsed?.ts) return null;

    const age = Date.now() - parsed.ts;

    if (age > AUTH_CACHE_TTL_MS) {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }

    return parsed.user || null;
  } catch {
    return null;
  }
}

export function writeCachedAuthUser(user: CachedAuthUser | null) {
  try {
    const payload: CachePayload = {
      ts: Date.now(),
      user,
    };

    sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(payload));
  } catch {}
}

export async function fetchCachedAuthUser(
  url: string,
  options?: RequestInit
): Promise<CachedAuthUser | null> {
  let networkFailure = false;

  // IMPORTANT:
  // Always verify backend session truth first.
  // Old cached admin/free users caused cross-account bleed.
  try {
    const headers = new Headers(
      options?.headers || {}
    );

    if (!headers.has("Accept")) {
      headers.set(
        "Accept",
        "application/json"
      );
    }

    const token = getUnifiedToken();

    if (
      token &&
      !headers.has("Authorization")
    ) {
      headers.set(
        "Authorization",
        `Bearer ${token}`
      );
    }

    const res = await fetch(
      resolveAuthUrl(url),
      {
        ...(options || {}),
        credentials: "include",
        cache: "no-store",
        headers,
      }
    );

    if (res.ok) {
      const data = await res.json().catch(() => null);

      const user = data?.user || data || null;

      if (user) {
        writeCachedAuthUser(user);
        writeLastVerifiedOfflineUser(user);
        return user;
      }
    }
  } catch {
    networkFailure = true;
  }

  // A verified offline identity may be used only when the
  // backend could not be reached at all. Normal 401, 403,
  // and 404 responses never receive this fallback.
  if (networkFailure) {
    const offlineUser =
      readLastVerifiedOfflineUser();

    if (offlineUser) {
      return offlineUser;
    }
  }

  return null;
}
